import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  deleteFoundDocument,
  getFoundReportAsyncThunk,
  passFoundItemPendingRequest,
  rejectFoundItemPendingRequest,
  updateFoundItemisurgent,
  uploadFoundDocument,
  getFoundReportVersionAsyncThunk,
  updateFoundItemByAdmin,
  discardFoundItem,
  updateFoundItemStatus,
  updateFoundItemCaseManager,
} from "./foundReportAsyncThunk";
import { Report, User } from "../../../types";
import { toast } from "react-toastify";
import { PrematchedFoundItems } from "../../../types/PreMatchedFoundItems";
import { FoundReport } from "../../../types/FoundItemTypes";
import { AdminUser } from "../user/types";

export interface ReportState {
  data: FoundReport | null;
  versionData: FoundReport | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  loading: boolean;
  preMatchedFoundItems: PrematchedFoundItems[] | null;
}

const initialState: ReportState = {
  data: null,
  versionData: null,
  status: "idle",
  error: null,
  loading: false,
  preMatchedFoundItems: null,
};

interface SetKeyValuePayload<K extends keyof ReportState> {
  key: K;
  value: ReportState[K];
}

const foundReportSlice = createSlice({
  name: "foundReport",
  initialState,
  reducers: {
    setKeyValue: <K extends keyof ReportState>(state: ReportState, action: PayloadAction<SetKeyValuePayload<K>>) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    clearVersionData: (state) => {
      state.versionData = null;
    },
    updateItemCaseManager: (state, action: PayloadAction<{ caseManager: AdminUser | undefined }>) => {
      if (state?.data && action?.payload?.caseManager) {
        state.data.caseManager = { ...action.payload.caseManager };
        state.data.caseManagerName = `${action.payload.caseManager.firstName} ${action.payload.caseManager.lastName}`;
      }
    },
    updateFoundReportStatus: (state, action: PayloadAction<{ status: number }>) => {
      if (state?.data?.id) {
        state.data.status = action.payload.status;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      //reportAsyncThunk
      .addCase(getFoundReportAsyncThunk.pending, (state) => {
        state.status = "loading";
        state.data = null;
        state.versionData = null;
        state.error = null;
      })
      .addCase(getFoundReportAsyncThunk.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(getFoundReportAsyncThunk.rejected, (state, action) => {
        state.status = "failed";
        toast.error(action.payload || "Failed to get Report Data.");
        state.error = action.payload || "An error occurred";
      })
      // Upload Document
      .addCase(uploadFoundDocument.fulfilled, (state, action) => {
        if (state.data) {
          state.data = { ...state.data, ...action.payload };
        }
        toast.success("Document uploaded successfully");
      })
      // Delete Document
      .addCase(deleteFoundDocument.fulfilled, (state, action) => {
        if (state.data) {
          state.data.documents = state.data.documents.filter((document) => document.id === action.payload);
        }
        toast.success("Document deleted successfully");
      })
      //Update Item as Urgent
      .addCase(updateFoundItemisurgent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFoundItemisurgent.fulfilled, (state, action) => {
        if (action.payload && state?.data?.id) {
          state.data = { ...state.data, isUrgent: action.payload?.isUrgent };
        }
        state.loading = false;
        toast.success(`Item ${action.payload?.isUrgent ? "Mark as" : "Remove from"} urgent successfully`);
      })
      .addCase(updateFoundItemisurgent.rejected, (state, action: any) => {
        state.loading = false;
        toast.error(action?.payload || "Failed to update mark as urgent.");
        state.error = action.payload || "Failed to update item details.";
      })
      //Pass pending request
      .addCase(passFoundItemPendingRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(passFoundItemPendingRequest.fulfilled, (state, action) => {
        if (action.payload && state?.data?.id) {
          state.data = { ...state.data, isUrgent: action.payload?.isUrgent };
        }
        state.loading = false;
        // toast.success(`Item update passed successfully`);
      })
      .addCase(passFoundItemPendingRequest.rejected, (state, action: any) => {
        state.loading = false;
        const errMsg = action?.payload || "Failed to pass the updated item details.";
        toast.error(errMsg);
        state.error = errMsg;
      })
      //Reject pending request
      .addCase(rejectFoundItemPendingRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectFoundItemPendingRequest.fulfilled, (state, action) => {
        if (action.payload && state?.data?.id) {
          state.data = { ...state.data, isUrgent: action.payload?.isUrgent };
        }
        state.loading = false;
        toast.success(`Request rejected successfully`);
      })
      .addCase(rejectFoundItemPendingRequest.rejected, (state, action: any) => {
        state.loading = false;
        const errMsg = action?.payload || "Failed to reject request.";
        toast.error(errMsg);
        state.error = errMsg;
      })
      .addCase(getFoundReportVersionAsyncThunk.pending, (state) => {
        state.loading = true;
        state.status = "loading";
        state.error = null;
      })
      .addCase(getFoundReportVersionAsyncThunk.fulfilled, (state, action) => {
        state.versionData = action.payload;
        state.loading = false;
        state.status = "succeeded";
      })
      .addCase(getFoundReportVersionAsyncThunk.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error = action.payload || "Failed to fetch version data";
        toast.error(action.payload || "Failed to fetch version data");
      })
      // updateFoundItemByAdmin
      .addCase(updateFoundItemByAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFoundItemByAdmin.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
        toast.success("Found item updated successfully");
      })
      .addCase(updateFoundItemByAdmin.rejected, (state, action) => {
        state.loading = false;
        const errMsg = action.payload || "Failed to update lost item by admin.";
        toast.error(errMsg);
        state.error = errMsg;
      })
      //Discard found item
      .addCase(discardFoundItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(discardFoundItem.fulfilled, (state, action) => {
        if (state.data) {
          state.data = null;
        }
        toast.success("Found item discarded successfully");
        state.loading = false;
      })
      .addCase(discardFoundItem.rejected, (state, action) => {
        const errMsg = "Failed to discard found item.";
        toast.error(action.payload || errMsg);
        state.loading = false;
      })
      //Update found item status
      .addCase(updateFoundItemStatus.pending, (state, action) => {})
      .addCase(updateFoundItemStatus.fulfilled, (state, action) => {})
      .addCase(updateFoundItemStatus.rejected, (state, action: any) => {
        // No direct state changes here, handled in the caller
        toast.error(action?.payload);
      })
      //Update case manager
      .addCase(updateFoundItemCaseManager.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateFoundItemCaseManager.fulfilled, (state, action) => {
        if (state?.data) {
          state.data = { ...state.data, caseManager: action.payload };
        }
        state.loading = false;
      })
      .addCase(updateFoundItemCaseManager.rejected, (state, action: PayloadAction<any>) => {
        const errMsg = action.payload || "Failed to update case manager.";
        toast.error(errMsg);
        state.error = errMsg;
        state.loading = false;
      });
  },
});

export const { setKeyValue, clearVersionData, updateItemCaseManager, updateFoundReportStatus } = foundReportSlice.actions;
export default foundReportSlice.reducer;
